import {API_URL} from "../settings/local_settings";
import {LocalStorage} from "./LocalStorage";
import {DASHBO_CLIENT_HEADER, DASHBO_CLIENT_HEADER_VALUE} from "./APIClient";
import {ClarityRecorder} from "./clarity/ClarityRecorder";

// Tracking de navegación de pantallas (proyecto Reemplazo de Intercom, Fase 1.5A).
// Buffer en memoria {screen: count}; flush cada 60s SOLO si hay algo que enviar, con swap
// sincrónico del buffer (un doble-flush ve el buffer vacío => sin duplicados por diseño).
// En pagehide flushea con keepalive. SIN retries: si el POST falla, se descarta (log en dev).
// Endpoint neutro POST {API_URL}app-usage/ con el token DRF del user (fetch propio, no APIClient).

const FLUSH_INTERVAL_MS = 60000; // 60s
const APP_USAGE_PATH = "app-usage/";

class ScreenTrackerImpl {
    constructor() {
        this._buffer = {};            // {screen: count}
        this._flushTimer = null;
        this._pagehideHandler = null;
        this._started = false;
        this._currentScreen = null;
    }

    // Registra una vista de pantalla. Incrementa el count del screen en el buffer.
    recordView(screen) {
        if (!screen) {
            return;
        }
        this._currentScreen = screen;
        this._buffer[screen] = (this._buffer[screen] || 0) + 1;
        this._ensureStarted();
    }

    // Cuenta algo que pasó DENTRO de una pantalla (se mostró un aviso de límite, se hizo clic en su
    // CTA) por el mismo canal y con el mismo formato de nombre. No toca `currentScreen`: los links a
    // la ayuda tienen que seguir diciendo en qué pantalla se abrieron. Si la sesión se graba en
    // Clarity, el mismo nombre va como evento: el panel filtra las grabaciones por él (UD-7). Los
    // nombres son fijos; uno que lleve un dato no puede pasar por acá.
    recordEvent(name) {
        if (!name) {
            return;
        }
        this._buffer[name] = (this._buffer[name] || 0) + 1;
        this._ensureStarted();
        ClarityRecorder.event(name);
    }

    // Última pantalla vista: la usan los links a la ayuda para decir desde dónde se abrieron.
    currentScreen() {
        return this._currentScreen;
    }

    // Arranca el timer de flush + el listener de pagehide una sola vez (lazy: recién cuando
    // hay algo que trackear). Guarda contra entornos sin window (tests/SSR).
    _ensureStarted() {
        if (this._started) {
            return;
        }
        this._started = true;

        if (typeof window === 'undefined') {
            return;
        }

        this._flushTimer = setInterval(() => this.flush(false), FLUSH_INTERVAL_MS);

        this._pagehideHandler = () => this.flush(true);
        window.addEventListener('pagehide', this._pagehideHandler);
    }

    // Toma el buffer actual, lo resetea SINCRÓNICAMENTE, y recién ahí envía. Un segundo flush
    // que corra en el medio ve el buffer ya vacío => no reenvía nada.
    // useKeepalive=true en pagehide (el request sobrevive a la descarga de la página).
    flush(useKeepalive = false) {
        const screens = Object.keys(this._buffer);
        if (screens.length === 0) {
            return;
        }

        // Swap sincrónico: capturar el buffer y vaciarlo ANTES del fetch async.
        const batch = this._buffer;
        this._buffer = {};

        const token = LocalStorage.fetchUserToken();
        if (!token) {
            // Sin sesión: no hay a quién atribuirlo. El buffer ya quedó vaciado arriba.
            return;
        }

        const views = screens.map((screen) => ({screen, count: batch[screen]}));
        this._send(views, token, useKeepalive);
    }

    _send(views, token, useKeepalive) {
        const url = API_URL + APP_USAGE_PATH;
        const options = {
            method: 'POST',
            headers: {
                'Authorization': 'Token ' + token,
                'Content-Type': 'application/json',
                [DASHBO_CLIENT_HEADER]: DASHBO_CLIENT_HEADER_VALUE,
            },
            body: JSON.stringify({views}),
        };
        if (useKeepalive) {
            options.keepalive = true;
        }

        // SIN retries: un fallo se descarta (pérdida ocasional aceptable, evita duplicación).
        try {
            const result = fetch(url, options);
            if (result && typeof result.catch === 'function') {
                result.catch((error) => this._logError(error));
            }
        } catch (error) {
            this._logError(error);
        }
    }

    _logError(error) {
        if (process.env.NODE_ENV === 'development') {
            // eslint-disable-next-line no-console
            console.warn('[ScreenTracker] flush failed (descartado, sin retry):', error);
        }
    }

    // Utilidad para tests: desmonta timer + listener y limpia el buffer.
    _reset() {
        if (this._flushTimer) {
            clearInterval(this._flushTimer);
            this._flushTimer = null;
        }
        if (this._pagehideHandler && typeof window !== 'undefined') {
            window.removeEventListener('pagehide', this._pagehideHandler);
            this._pagehideHandler = null;
        }
        this._buffer = {};
        this._started = false;
    }
}

// Singleton — una sola instancia para toda la app.
export const ScreenTracker = new ScreenTrackerImpl();
